import { HubConnection } from "@microsoft/signalr";
import { useCallback, useEffect, useRef } from "react";
import { useSystemStateStore } from "../store/useSystemStateStore";
import { SignalREvents } from "../types/signalr";
import { EdgeEndpointConfiguration, buildAnonymousHub, getEndpointConfiguration, getInitialSyncStatus, getSystemState } from "../utils";

let signalRHub: HubConnection | null = null;

export const SystemStateTracker = () => {
  const {
    api: apiHost,
    hubAnonymous: hubUrl,
    isCloud
  } = getEndpointConfiguration() as EdgeEndpointConfiguration;

  const {
    setInitialSyncStatus,
    setSystemState,
    updateSystemState
  } = useSystemStateStore();

  const isFetchingSystemState = useRef<boolean>(false);
  const isFetchingInitialSyncStatus = useRef<boolean>(false);
  
  const fetchInitialSystemState = useCallback(() => {
    if (isCloud || isFetchingSystemState.current) {
      return;
    }

    const fetchSystemState = async () => {
      const state = await getSystemState(apiHost);
      setSystemState(state);
      isFetchingSystemState.current = false;
    };

    isFetchingSystemState.current = true;
    fetchSystemState();
  }, [apiHost, isCloud, setSystemState]);

  const fetchInitialSyncStatus = useCallback(() => {
    if (isCloud || isFetchingInitialSyncStatus.current) {
      return;
    }

    const fetchSyncStatus = async () => {
      const initialSyncStatus = await getInitialSyncStatus(apiHost);
      setInitialSyncStatus(initialSyncStatus);
      isFetchingInitialSyncStatus.current = false;
    };

    isFetchingInitialSyncStatus.current = true;
    fetchSyncStatus();
  }, [apiHost, isCloud, setInitialSyncStatus]);

  // Load initial system state and sync status
  useEffect(() => fetchInitialSystemState(), [fetchInitialSystemState]);
  useEffect(() => fetchInitialSyncStatus(), [fetchInitialSyncStatus]);

  // Monitor state changes
  useEffect(() => {
    if (isCloud || signalRHub?.baseUrl !== hubUrl) {
      signalRHub?.stop();
      signalRHub = null;
    }

    if (isCloud) {
      return;
    }

    if (!signalRHub) {
      signalRHub = buildAnonymousHub(hubUrl);

      signalRHub.on(SignalREvents.SYSTEM_STATE_UPDATED, updateSystemState);

      // Refetch system state on reconnection
      signalRHub.onreconnected(() => {
        fetchInitialSystemState();
        fetchInitialSyncStatus();
      });

      signalRHub
        .start()
        .catch(reason => console.error(`Error connecting to SignalR hub at ${hubUrl}`, reason));
    }
  }, [fetchInitialSyncStatus, fetchInitialSystemState, hubUrl, isCloud, setSystemState, updateSystemState]);

  return <></>;
};